import { fftRadix2 } from './fft';

export interface HpsOptions {
  sampleRate: number;
  bufferSize?: number;
  maxNotes?: number;
  minHz?: number;
  maxHz?: number;
  harmonics?: number;
  peakDrop?: number; // in natural log units
}

export interface HpsPeak {
  hz: number;
  amp: number;
  score: number;
}

export const createHpsDetector = (opts: HpsOptions) => {
  const sampleRate = opts.sampleRate;
  const size = opts.bufferSize ?? 2048;
  const maxNotes = opts.maxNotes ?? 4;
  const minHz = opts.minHz ?? 50;
  const maxHz = opts.maxHz ?? 2000;
  const harmonics = opts.harmonics ?? 4;
  const peakDrop = opts.peakDrop ?? 6;

  const half = size >> 1;
  const window = new Float32Array(size);
  for (let i = 0; i < size; i++) {
    window[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (size - 1));
  }

  const re = new Float32Array(size);
  const im = new Float32Array(size);
  const mag = new Float32Array(half);
  const logMag = new Float32Array(half);
  const hps = new Float32Array(half);

  const minBin = Math.max(1, Math.floor((minHz * size) / sampleRate));
  const maxBin = Math.min(half - 2, Math.floor((maxHz * size) / sampleRate));

  const binToHz = (bin: number) => (bin * sampleRate) / size;

  const parabolicPeak = (arr: Float32Array, idx: number) => {
    const y0 = arr[idx - 1];
    const y1 = arr[idx];
    const y2 = arr[idx + 1];
    const denom = y0 - 2 * y1 + y2;
    if (denom === 0) return idx;
    return idx + (0.5 * (y0 - y2)) / denom;
  };

  const isNearMultiple = (f: number, base: number) => {
    const ratio = f / base;
    const nearest = Math.round(ratio);
    if (nearest < 2) return false;
    return Math.abs(ratio - nearest) < 0.03;
  };

  const isNearSame = (f: number, other: number) => {
    const semitone = Math.pow(2, 1 / 12);
    const ratio = f / other;
    return ratio > 1 / semitone && ratio < semitone;
  };

  const estimatePeaks = (frame: Float32Array): HpsPeak[] => {
    if (frame.length < size) return [];

    for (let i = 0; i < size; i++) {
      re[i] = frame[i] * window[i];
      im[i] = 0;
    }

    fftRadix2(re, im);

    for (let k = 0; k < half; k++) {
      const m = Math.hypot(re[k], im[k]);
      mag[k] = m;
      logMag[k] = Math.log(m + 1e-12);
    }

    let maxVal = -Infinity;
    for (let k = minBin; k <= maxBin; k++) {
      let sum = logMag[k];
      for (let h = 2; h <= harmonics; h++) {
        const kk = k * h;
        if (kk > maxBin) break;
        sum += logMag[kk];
      }
      hps[k] = sum;
      if (sum > maxVal) maxVal = sum;
    }

    const threshold = maxVal - peakDrop;
    const peaks: { bin: number; val: number }[] = [];

    for (let k = minBin + 1; k < maxBin - 1; k++) {
      const v = hps[k];
      if (v < threshold) continue;
      if (v > hps[k - 1] && v > hps[k + 1]) {
        peaks.push({ bin: k, val: v });
      }
    }

    peaks.sort((a, b) => b.val - a.val);

    const results: HpsPeak[] = [];
    for (let i = 0; i < peaks.length && results.length < maxNotes; i++) {
      const bin = peaks[i].bin;
      const refinedBin = parabolicPeak(hps, bin);
      const freq = binToHz(refinedBin);
      const baseBin = Math.max(0, Math.min(half - 2, Math.floor(refinedBin)));
      const frac = refinedBin - baseBin;
      const amp = mag[baseBin] * (1 - frac) + mag[baseBin + 1] * frac;

      let skip = false;
      for (const p of results) {
        if (isNearSame(freq, p.hz) || isNearMultiple(freq, p.hz) || isNearMultiple(p.hz, freq)) {
          skip = true;
          break;
        }
      }
      if (!skip) results.push({ hz: freq, amp, score: peaks[i].val });
    }

    return results;
  };

  return {
    estimatePeaks,
    estimateHzs(frame: Float32Array): number[] {
      return estimatePeaks(frame).map((p) => p.hz);
    },
  };
};
